import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LayoutServiceService } from '../../../services/layout.service';
import { Data, PageLayout, SectionLayout } from '../../../models/layout';
import { TableControllerService } from '../../../services/table-controller.service';
import { firstValueFrom } from 'rxjs';
import { GeneralService } from '../../../services/crud/general.service';
import { AlertService } from '../alert/alert.service';
import { ALERT_TYPE } from '../alert/alert.enumerate';
import { cloneDeep } from 'lodash';

@Component({
	selector: 'bulk-edit-dialog',
	templateUrl: './bulk-edit-dialog.component.html',
	styleUrls: ['./bulk-edit-dialog.component.scss']
})
export class BulkEditDialogComponent implements OnInit
{
	pageId: string;
	bulkEditPageId: string;
	selectedRows: any[];
	tableController: TableControllerService;
	apiParent: string;
	apiUrl: string;

	sections?: SectionLayout[];
	isLoading: boolean = true;
	isSaving: boolean = false;
	errorOccurred: boolean = false;
	formData: any = {};
	clearedFields: { [key: string]: boolean } = {};

	get firstSectionDatas(): Data[]
	{
		if (!this.sections?.length) return [];
		const firstSection = this.sections[0];
		if (!firstSection.dataset?.length) return [];
		return firstSection.dataset[0].datas ?? [];
	}

	constructor(
		public dialogRef: MatDialogRef<BulkEditDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { pageId: string; selectedRows: any[]; tableController: TableControllerService; apiParent: string; apiUrl: string },
		public layoutService: LayoutServiceService,
		private generalService: GeneralService,
		private alertService: AlertService
	)
	{
		dialogRef.disableClose = true;
		this.pageId = data.pageId;
		this.bulkEditPageId = data.pageId + '_BULK_EDIT';
		this.selectedRows = data.selectedRows;
		this.tableController = data.tableController;
		this.apiParent = data.apiParent ?? '';
		this.apiUrl = data.apiUrl ?? '';
	}

	async ngOnInit()
	{
		await this.getPageLayout();
	}

	private async getPageLayout(): Promise<void>
	{
		try
		{
			const res: PageLayout | null = await firstValueFrom(this.layoutService.getPageLayout(this.bulkEditPageId));

			if (!res)
			{
				console.error('BulkEditDialog: invalid response or bad status code');
				this.errorOccurred = true;
				return;
			}

			const sections = res.layouts.flatMap(l => l.sectionLayouts);

			if (!sections.length)
			{
				console.error('BulkEditDialog: no sections found', res);
				this.errorOccurred = true;
				return;
			}

			this.sections = sections;
		}
		catch (error)
		{
			this.errorOccurred = true;
			console.error('BulkEditDialog: error while retrieving layout:', error);
		}
		finally
		{
			this.isLoading = false;
		}
	}

	onClearField(columnName: string, checked: boolean): void
	{
		if (checked)
		{
			this.formData[columnName] = null;
		}
	}

	async save()
	{
		// Collect form fields with a non-null, non-undefined value
		const filledFields = Object.entries(this.formData).filter(([, value]) => value !== null && value !== undefined && value !== '');

		// Collect fields explicitly marked for clearing
		const clearedFieldsList: [string, null | string][] = Object.entries(this.clearedFields)
			.filter(([, cleared]) => cleared)
			.map(([key]) =>
			{
				const col = this.firstSectionDatas.find(d => d.columnName === key);
				return [key, col?.type === 'DTY_TIME' || col?.type === 'DTY_DATE' ? null : ''];
			});

		const allFields = [...filledFields, ...clearedFieldsList];

		if (allFields.length === 0)
		{
			this.alertService.add({
				type: ALERT_TYPE.WARNING,
				message: 'No fields to update',
				timeout: 3000,
				selfClose: null
			});
			return;
		}

		// Apply values to each selected row
		const rowsToSave = this.selectedRows.map(row =>
		{
			const updatedRow = cloneDeep(row);
			allFields.forEach(([key, value]) =>
			{
				updatedRow[key] = value;
			});
			delete updatedRow.editing;
			delete updatedRow.checked;
			return updatedRow;
		});

		this.isSaving = true;

		try
		{
			const apiPath = this.apiParent + '/' + this.apiUrl;
			const res = await firstValueFrom(this.generalService.postData(apiPath, rowsToSave));

			if (res?.iResponseCode && res.iResponseCode !== 200)
			{
				this.alertService.add({
					type: ALERT_TYPE.DANGER,
					message: `${res.iResponseCode} - ${res.sResponseMessage}`,
					timeout: 5000,
					selfClose: null
				});
			}
			else
			{
				this.alertService.add({
					type: ALERT_TYPE.SUCCESS,
					message: res?.sResponseMessage ?? 'Saved successfully',
					timeout: 3000,
					selfClose: null
				});
				this.dialogRef.close(true);
			}
		}
		catch (err: any)
		{
			const errData = err?.error?.data;
			this.alertService.add({
				type: ALERT_TYPE.DANGER,
				message: errData ? `${errData.iResponseCode} - ${errData.sResponseMessage}` : 'An error occurred while saving',
				timeout: 5000,
				selfClose: null
			});
		}
		finally
		{
			this.isSaving = false;
		}
	}

	close()
	{
		this.dialogRef.close();
	}
}
